import { faPlus, faThumbtack } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { t as globalT } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Badge, Breadcrumbs, Group, Stack, Table, Text } from "@mantine/core";
import {
  AnchorLink,
  ButtonLink,
  LoadingSuspense,
} from "@sweco-se/reality-frontend";
import { createFileRoute } from "@tanstack/react-router";
import { type FC, useMemo } from "react";

import { api } from "@/api.ts";
import { levelColors } from "@/components/news/NewsPostRow.tsx";
import { useSuspenseQueryPostgrest } from "@/hooks/useSuspenseQueryPostgrest";

export const Route = createFileRoute("/admin/news/")({
  component: RouteComponent,
  head: () => ({
    meta: [
      {
        title: globalT`Sweco REALity` + " - " + globalT`News`,
      },
    ],
  }),
});

type NewsPostRow = {
  id: number;
  level: "info" | "warning";
  published: boolean;
  pinned: boolean;
  show_from: string | null;
  show_to: string | null;
  title: string;
};

const NewsList: FC = () => {
  const { data } = useSuspenseQueryPostgrest(
    useMemo(
      () =>
        api
          .from("news_post")
          .select("id,level,published,pinned,show_from,show_to,title")
          .order("created_at", { ascending: false }),
      [],
    ),
  );
  const { i18n } = useLingui();

  return (
    <Table>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Trans>Title</Trans>
          </Table.Th>
          <Table.Th>
            <Trans context="news level">Level</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Status</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Show from</Trans>
          </Table.Th>
          <Table.Th>
            <Trans>Show to</Trans>
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {(data as Array<NewsPostRow>).map((post) => (
          <Table.Tr key={post.id}>
            <Table.Td>
              <AnchorLink to="/admin/news/$id" params={{ id: String(post.id) }}>
                <Group gap="xs" wrap="nowrap">
                  {post.pinned && <FontAwesomeIcon icon={faThumbtack} />}
                  <Text>{post.title}</Text>
                </Group>
              </AnchorLink>
            </Table.Td>
            <Table.Td>
              <Badge color={levelColors[post.level]} variant="light">
                {post.level === "warning" ? (
                  <Trans context="news level">Warning</Trans>
                ) : (
                  <Trans context="news level">Info</Trans>
                )}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Badge color={post.published ? "green" : "gray"} variant="light">
                {post.published ? (
                  <Trans context="news status">Published</Trans>
                ) : (
                  <Trans context="news status">Draft</Trans>
                )}
              </Badge>
            </Table.Td>
            <Table.Td>
              {post.show_from ? i18n.date(post.show_from) : "-"}
            </Table.Td>
            <Table.Td>{post.show_to ? i18n.date(post.show_to) : "-"}</Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
};

function RouteComponent() {
  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-start">
        <Breadcrumbs>
          <Text>
            <Trans>Admin</Trans>
          </Text>
          <Text>
            <Trans>News</Trans>
          </Text>
        </Breadcrumbs>
        <ButtonLink
          to="/admin/news/create"
          leftSection={<FontAwesomeIcon icon={faPlus} />}
        >
          <Trans>Create</Trans>
        </ButtonLink>
      </Group>
      <LoadingSuspense>
        <NewsList />
      </LoadingSuspense>
    </Stack>
  );
}
